// @ts-nocheck

import * as turf from '@turf/turf';
import { extend, flatten, uniq } from 'lodash';
import { createSelector } from 'reselect';
import { isCTVCampaign, isDigitalCampaign, isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import {
  getInsightsConfig as getConfig,
  getInsightItems,
  getInsightsFilters
} from 'sonata-static-storage/lib/helpers/insights.helper';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { digitalConversionKpis } from 'sonata-static-storage/lib/samples/statsPreferences.samples';
import { PricingModelEnum } from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import {
  campaignsHaveAppInstall,
  campaignsHaveAudio,
  campaignsHaveBaseline,
  campaignsHaveContextualBidding,
  campaignsHaveCTVEvents,
  campaignsHaveDCO,
  campaignsHaveDigitalConversions,
  campaignsHaveDriveToStore,
  campaignsHaveFootfallAttribution,
  campaignsHaveLegacyFootfallAttribution,
  campaignsHaveLumenAttention,
  campaignsHaveVideo,
  campaignsHaveViewability,
  getDoohDemographyAgesItem,
  getPermissions,
  havePricingModel
} from '@/helpers/reporting.helpers';
import { getUniqueCountries } from '@/modules/reporting/ReportingInsights/reporting.helpers';
import { getCampaignsType } from '@/ts-helpers/campaign.helpers';
import { getUserRoles } from './resources.selectors';
import { createDeepEqualSelector } from './selectors.helper';
import { getCurrentUser } from './user.selectors';

export const getReportingStore = store => store.reporting;

export const getSelectedCampaigns = createSelector([getReportingStore], moduleStore => moduleStore.selectedCampaigns);

export const getCampaignEvents = createSelector([getReportingStore], moduleStore =>
  uniq(flatten(moduleStore.campaignEvents))
);

export const getHeatmapData = createSelector(
  [getReportingStore, (store, mapId) => mapId],
  (moduleStore, mapId) => moduleStore.heatmapData[mapId]
);

export const getEventsByGeoH = createSelector(
  [getReportingStore, (store, mapId) => mapId],
  (moduleStore, mapId) => moduleStore.eventsByGeoH[mapId]
);

export const getIndividualEvents = createSelector(
  [getReportingStore, (store, mapId) => mapId],
  (moduleStore, mapId) => moduleStore.individualEvents[mapId]
);

export const getMapBounds = createSelector([getSelectedCampaigns], selectedCampaigns => {
  const campaignWithMapBoundsExist = selectedCampaigns.find(selectedCampaign => {
    return selectedCampaign.mapBoundaries && selectedCampaign.mapBoundaries.northWest;
  });
  if (campaignWithMapBoundsExist) {
    const mapBounds = {
      northWest: {
        lat: selectedCampaigns[0].mapBoundaries.northWest.lat,
        lng: selectedCampaigns[0].mapBoundaries.northWest.lng
      },
      southEast: {
        lat: selectedCampaigns[0].mapBoundaries.southEast.lat,
        lng: selectedCampaigns[0].mapBoundaries.southEast.lng
      }
    };
    extend([], [selectedCampaigns[0]]).forEach(selectedCampaign => {
      const { northWest, southEast } = selectedCampaign.mapBoundaries;
      if (mapBounds.northWest.lng > northWest.lng) {
        mapBounds.northWest.lng = northWest.lng;
      }
      if (mapBounds.northWest.lat < northWest.lat) {
        mapBounds.northWest.lat = northWest.lat;
      }
      if (mapBounds.southEast.lng < southEast.lng) {
        mapBounds.southEast.lng = southEast.lng;
      }
      if (mapBounds.southEast.lat > southEast.lat) {
        mapBounds.southEast.lat = southEast.lat;
      }
    });
    return turf.bboxPolygon([
      mapBounds.southEast.lng,
      mapBounds.northWest.lat,
      mapBounds.northWest.lng,
      mapBounds.southEast.lat
    ]);
  }
  return turf.bboxPolygon([180, 90, -180, -90]);
});

export const getAggregatedStats = createSelector([getReportingStore], moduleStore => moduleStore.aggregatedStats);

export const getHasViewability = createSelector(
  [getSelectedCampaigns, getAggregatedStats],
  (selectedCampaigns, aggregatedStats) => campaignsHaveViewability(selectedCampaigns, aggregatedStats)
);

export const getHasLumenAttention = createSelector([getAggregatedStats], aggregatedStats =>
  campaignsHaveLumenAttention(aggregatedStats)
);

export const getSummarySection = createSelector([getReportingStore], moduleStore => moduleStore.sections.summary);

export const getCampaignsScreens = createSelector([getReportingStore], moduleStore => moduleStore.screens);

export const getHavePPE = createSelector([getReportingStore], moduleStore =>
  havePricingModel(moduleStore.selectedCampaigns, PricingModelEnum.ppe)
);

export const getHavePPPV = createSelector([getReportingStore], moduleStore =>
  havePricingModel(moduleStore.selectedCampaigns, PricingModelEnum.pppv)
);

export const getHasDigitalConversions = createSelector(
  [getSelectedCampaigns, getCurrentUser, getUserRoles],
  (selectedCampaigns, user, userRoles) => {
    return (
      checkUserResourcePermissions('reportingDigitalConversions', user, userRoles) &&
      campaignsHaveDigitalConversions(selectedCampaigns)
    );
  }
);

export const getHasAppsInstall = createSelector([getAggregatedStats], stats => {
  return campaignsHaveAppInstall(stats);
});
export const getDigitalConversionEvents = createSelector(
  [getAggregatedStats, getHasDigitalConversions],
  (stats, hasDigitalConversions) => {
    if (hasDigitalConversions) {
      return digitalConversionKpis.filter(kpi => {
        return (
          kpi.indexOf('cp') === -1 &&
          kpi !== 'visit_bounce_rate' &&
          kpi !== 'visit_retention_rate' &&
          kpi in stats &&
          stats[kpi] &&
          stats[kpi] > 0
        );
      });
    }
    return [];
  }
);

export const getMapLoading = createSelector(
  [getReportingStore, (store, mapId) => mapId],
  (moduleStore, mapId) => moduleStore.mapLoading[mapId] || false
);

export const getAds = createSelector([getReportingStore], moduleStore =>
  flatten(moduleStore.selectedCampaigns.map(campaign => campaign.ads))
);

export const getReportingType = createSelector([getSelectedCampaigns], selectedCampaigns => {
  if (selectedCampaigns.every(campaign => isCTVCampaign(campaign.type))) {
    return CampaignFamilyEnum.ctv;
  }

  return selectedCampaigns.some(campaign => isDigitalCampaign(campaign.type))
    ? selectedCampaigns.some(campaign => isDOOHCampaign(campaign.type))
      ? 'both'
      : CampaignFamilyEnum.standard
    : CampaignFamilyEnum.dooh;
});

export const getHasCTVEvents = createSelector(
  [getAggregatedStats, getSelectedCampaigns],
  (stats, selectedCampaigns) => {
    return campaignsHaveCTVEvents(selectedCampaigns, stats);
  }
);

export const getHasVideo = createSelector(
  [getAggregatedStats, getSelectedCampaigns, getReportingType],
  (stats, selectedCampaigns, reportingType) => {
    return !isDOOHCampaign(reportingType) && campaignsHaveVideo(selectedCampaigns, stats);
  }
);

export const getHasAudio = createSelector(
  [getAggregatedStats, getSelectedCampaigns, getReportingType],
  (stats, selectedCampaigns, reportingType) => {
    return !isDOOHCampaign(reportingType) && campaignsHaveAudio(selectedCampaigns, stats);
  }
);

export const getHasFootfallAttribution = createSelector([getSelectedCampaigns], selectedCampaigns => {
  return campaignsHaveFootfallAttribution(selectedCampaigns);
});

export const getHasBaseline = createSelector([getSelectedCampaigns], selectedCampaigns => {
  return campaignsHaveBaseline(selectedCampaigns);
});

export const getHasDriveToStore = createSelector([getSelectedCampaigns], selectedCampaigns => {
  return campaignsHaveDriveToStore(selectedCampaigns);
});

export const getHasLegacyFootfallAttribution = createSelector([getSelectedCampaigns], selectedCampaigns => {
  return campaignsHaveLegacyFootfallAttribution(selectedCampaigns);
});

export const getFootfallAttributionCampaigns = createSelector(
  [getSelectedCampaigns, (store, filteredCampaigns) => filteredCampaigns],
  (selectedCampaigns, filteredCampaigns) =>
    selectedCampaigns.reduce((acc, actualItem) => {
      if (actualItem.hasNewAttributionStats && filteredCampaigns.includes(actualItem.id)) {
        return [...acc, actualItem.id];
      }
      return acc;
    }, [])
);

export const getAnalyticalView = createSelector(
  [getReportingStore, (_, section) => section],
  (moduleStore, section) => moduleStore.sections[section].analyticalViewStats
);

function checkPermissions(user, userRoles, permissions = []) {
  return permissions.every(permission => checkUserResourcePermissions(permission, user, userRoles));
}

export const getHasInsightsPermissions = createSelector([getCurrentUser, getUserRoles], (user, userRoles) => {
  return checkUserResourcePermissions('reportingInsights', user, userRoles);
});

export const getHasReportingFootfallAttributionMapPermissions = createSelector(
  [getCurrentUser, getUserRoles],
  (user, userRoles) => {
    return checkUserResourcePermissions('reportingFootfallAttributionInsights', user, userRoles);
  }
);

export const getHasReportingStatsV2Permissions = createSelector([getCurrentUser, getUserRoles], (user, userRoles) => {
  return checkUserResourcePermissions('reportingStatsV2', user, userRoles);
});

export const getHasReportingAssistantPermissions = createSelector([getCurrentUser, getUserRoles], (user, userRoles) => {
  return checkUserResourcePermissions('reportingAiAssistant', user, userRoles);
});

function getBoxesToExclude(campaigns) {
  const boxToExclude = [];
  if (!campaigns.some(campaign => ['hybrid', 'pmp'].includes(campaign.mediaType))) {
    boxToExclude.push('deals');
  }
  if (!campaigns.some(campaign => Object.values(campaign.temporalPacing).some(value => value))) {
    boxToExclude.push('schedule');
  }
  if (!campaigns.some(campaign => campaign.zoneGroups.some(({ sonataLiLocations }) => sonataLiLocations))) {
    boxToExclude.push('affinityIndex');
  }
  if (!campaigns.some(campaign => campaign.hasAttribution)) {
    boxToExclude.push('attrByStore', 'attrByCartography');
  }
  if (!campaignsHaveContextualBidding(campaigns)) {
    boxToExclude.push('contextualBidding');
  }
  if (!campaignsHaveDCO(campaigns)) {
    boxToExclude.push('dco');
  }
  boxToExclude.push('lumenAttentivePerMille', 'lumenAvgViewTime');
  return boxToExclude;
}

function insightsFilterByPermissions(insightConfig, boxToExclude, campaigns, user, userRoles) {
  const { campaignType, id, permissions } = insightConfig;
  return (
    !boxToExclude.includes(id) &&
    (!campaignType || campaigns.some(({ type }) => campaignType.includes(type))) &&
    checkPermissions(user, userRoles, permissions)
  );
}

export const getInsightsConfig = createDeepEqualSelector(
  [getSelectedCampaigns, getCurrentUser, getUserRoles],
  (campaigns, user, userRoles) => {
    const boxToExclude = getBoxesToExclude(campaigns);
    const filtersBoxes = {};
    const type = getCampaignsType(campaigns);

    const filteredConfig = getConfig(type).map(row => {
      const items = [
        ...row.items.filter(item => insightsFilterByPermissions(item, boxToExclude, campaigns, user, userRoles))
      ];
      filtersBoxes[row.type] = [...(filtersBoxes[row.type] || []), ...items];
      return {
        ...row,
        items
      };
    });
    return {
      config: filteredConfig,
      filters: getInsightsFilters().filter(
        ({ value }) => Array.isArray(filtersBoxes[value]) && filtersBoxes[value].length
      )
    };
  }
);

export const getInsightFiltered = createDeepEqualSelector(
  [getSelectedCampaigns, getCurrentUser, getUserRoles, (store, insightIds) => insightIds],
  (campaigns, user, userRoles, insightIds) => {
    const boxToExclude = getBoxesToExclude(campaigns);
    const items = insightIds.map(id => getInsightItems()[id]);
    return items.filter(item => insightsFilterByPermissions(item, boxToExclude, campaigns, user, userRoles));
  }
);

export const getChangeLogData = createSelector([getReportingStore], moduleStore => moduleStore.changeLogData);

export const getTotalAvgModes = createSelector([getReportingStore], moduleStore => moduleStore.totalAvgModes);

export const getStaticMode = createSelector([getReportingStore], moduleStore => moduleStore.staticMode);

export const getDoohDemographyAges = createSelector([getAggregatedStats], stats => {
  return getDoohDemographyAgesItem(stats);
});

export const getIsAgeAndGenderVisible = createSelector(
  [getAggregatedStats, getCurrentUser, getUserRoles, getReportingType],
  (stats, user, userRoles, reportingType) => {
    const { doohPermission, hasDoohDemographyStats } = getPermissions(user, userRoles, stats);
    return !isDigitalCampaign(reportingType) && doohPermission && hasDoohDemographyStats;
  }
);

export const getReportingKpiStats = createSelector(
  [getReportingStore, (store, groupBy) => groupBy],
  (moduleStore, groupBy) => {
    const {
      sections: {
        progress: { kpiStats }
      }
    } = moduleStore;
    if (kpiStats[groupBy]) {
      return kpiStats[groupBy];
    }
    return null;
  }
);

export const getSharedReports = createSelector([getReportingStore], moduleStore => moduleStore.sharedReports);

export const getHasAdWithQR = createSelector([getSelectedCampaigns], selectedCampaigns => {
  return selectedCampaigns.some(item => item.ads.some(s => s.adOptions?.qrConfig?.enabled));
});

export const includeHouseHoldImpacts = createSelector(
  [getReportingStore],
  moduleStore =>
    !!moduleStore.cartography && moduleStore.cartography.every(({ reporting }) => !!reporting?.householdImpactsEnabled)
);

export const someCountryHasHouseHoldImpacts = createSelector(
  [getReportingStore],
  moduleStore =>
    !!moduleStore.cartography && moduleStore.cartography.some(({ reporting }) => !!reporting?.householdImpactsEnabled)
);

export const getImpressionsKpiChartSelector = createDeepEqualSelector(
  [
    getCurrentUser,
    getUserRoles,
    getHasViewability,
    getHasLumenAttention,
    getHasVideo,
    getHasAudio,
    getHasLegacyFootfallAttribution,
    getHasDigitalConversions,
    getReportingType,
    getHasAdWithQR,
    getHavePPE,
    getHavePPPV,
    getHasCTVEvents,
    includeHouseHoldImpacts
  ],
  (
    user,
    userRoles,
    hasViewability,
    hasLumenAttention,
    hasVideo,
    hasAudio,
    hasLegacyFootfallAttribution,
    hasDigitalConversions,
    reportingType,
    hasAdWithQR,
    isPPE,
    isPPPV,
    hasCTVEvents,
    houseHoldImpactsEnabled
  ) => {
    const {
      avgFootfallAttributionPermission,
      digitalConversions,
      footfallAttributionPermission,
      householdImpactsPermission,
      mediaFeePermission,
      viewabilityPermission
    } = getPermissions(user, userRoles);
    const includeHouseholdImpacts = houseHoldImpactsEnabled && householdImpactsPermission;
    const isDOOH = isDOOHCampaign(reportingType);
    const isCTV = isCTVCampaign(reportingType);
    const isDigital = reportingType === CampaignFamilyEnum.standard;
    const metric = {
      actionAudioComplete: hasAudio && !isDOOH,
      actionVideoComplete: hasVideo && !isDOOH,
      clicks: !isDOOH,
      engagements: isPPE,
      footfallAttribution:
        hasLegacyFootfallAttribution && (avgFootfallAttributionPermission || footfallAttributionPermission),
      householdImpacts: includeHouseholdImpacts && hasCTVEvents,
      impressions: true,
      mediaFee: mediaFeePermission,
      pages_viewed: isPPPV,
      playouts: isDOOH,
      qr_scan: hasAdWithQR,
      storeVisitsCost:
        hasLegacyFootfallAttribution && (avgFootfallAttributionPermission || footfallAttributionPermission),
      total_viewers: includeHouseholdImpacts && isCTV,
      viewabilityInView: hasViewability && viewabilityPermission && !isDOOH,
      visitDigitalConversion: hasDigitalConversions && digitalConversions && !isDOOH
    };
    const kpi = {
      atr: hasAudio && !isDOOH,
      attn_lumen_attentive_per_mille: hasLumenAttention && !isDOOH,
      attn_lumen_avg_view_time: hasLumenAttention && !isDOOH,
      attn_lumen_viewed_rate: hasLumenAttention && !isDOOH,
      ctr: !isCTV || !includeHouseholdImpacts,
      etr: isPPE,
      hctr: isCTV && includeHouseholdImpacts,
      hvtr: hasVideo && isCTV && includeHouseholdImpacts,
      pvtr: isPPPV,
      storeVisitsRate:
        hasLegacyFootfallAttribution && (avgFootfallAttributionPermission || footfallAttributionPermission),
      viewabilityInViewRate: hasViewability && viewabilityPermission && !isDOOH,
      visitRetentionRate: hasDigitalConversions && digitalConversions && !isDOOH,
      vtr: hasVideo && isDigital
    };
    const metricOptions = Object.keys(metric).filter(itemKey => metric[itemKey]);
    const kpisOptions = Object.keys(kpi).filter(itemKey => kpi[itemKey]);
    return {
      kpisOptions,
      metricOptions
    };
  }
);

export const getInsightsUserConfig = createSelector([getReportingStore], moduleStore => moduleStore.insights.config);

export const getInsightsRawData = createSelector([getReportingStore], moduleStore => moduleStore.insights.rawData);

export const getAdvancedFiltersEnabled = createSelector(
  [getReportingStore],
  moduleStore => moduleStore.advancedFiltersEnabled
);

export const getInsightsItemStats = createSelector(
  [getReportingStore, (store, insightsId) => insightsId],
  (moduleStore, insightsId) => moduleStore.insights.stats[insightsId]
);

export const getReportingInsightsSectionOption = createSelector(
  [getReportingStore],
  moduleStore => moduleStore.insightsSectionOption
);

export const getReportingCampaignLinesInfo = createSelector(
  [getReportingStore],
  moduleStore => moduleStore.showCampaignLinesInfo
);

export const getCountries = createSelector([getReportingStore], moduleStore =>
  getUniqueCountries(moduleStore.selectedCampaigns)
);

export const getCountriesWithEvents = createSelector(
  [getReportingStore],
  moduleStore => moduleStore.countriesWithEvents
);

export const getDefaultMapBoundingBox = createSelector([getReportingStore], moduleStore =>
  moduleStore.sections.map.defaultBoundingBox
    ? turf.bboxPolygon(moduleStore.sections.map.defaultBoundingBox)
    : undefined
);

export const getCampaignItemDetailsStats = createSelector(
  [getSummarySection],
  summarySection => summarySection.campaignItemDetails.stats
);

export const getCampaignItemDetailsEventFamilies = createSelector(
  [getSummarySection],
  summarySection => summarySection.campaignItemDetails.filters.eventFamilies
);

export const getCampaignItemDetailsCampaignStatus = createSelector(
  [getSummarySection],
  summarySection => summarySection.campaignItemDetails.filters.campaignStatus
);

export const getCampaignItemDetailsTrendPeriod = createSelector(
  [getSummarySection],
  summarySection => summarySection.campaignItemDetails.filters.trendPeriod
);

export const getCampaignItemDetailsSearchText = createSelector(
  [getSummarySection],
  summarySection => summarySection.campaignItemDetails.filters.searchText
);

export const isCampaignItemDetailsOnlyTopCampaignsEnabled = createSelector(
  [getSummarySection],
  summarySection => summarySection.campaignItemDetails.onlyTopCampaigns
);

export const isCampaignItemDetailsAnalyticalViewEnabled = createSelector(
  [getSummarySection],
  summarySection => summarySection.campaignItemDetails.analyticalViewEnabled
);

export const getAggregatedStatsByCampaign = createSelector(
  [getReportingStore, (_, section) => section],
  (moduleStore, section) =>
    moduleStore.sections[section]?.aggregatedStatsByCampaign || moduleStore.aggregatedStatsByCampaign
);

export const getDownloadSummaryData = createSelector(
  [getReportingStore],
  moduleStore => moduleStore.downloadSummaryData
);

export const getDownloadInsightsData = createSelector(
  [getReportingStore],
  moduleStore => moduleStore.downloadInsightsData
);

export const getSectionsVisibility = createSelector([getReportingStore], moduleStore => moduleStore.sectionsVisibility);

export const getKpiStatsStatus = createSelector(
  [getReportingStore, (store, groupBy) => groupBy],
  (moduleStore, groupBy) => {
    const {
      sections: {
        progress: { kpiStats }
      }
    } = moduleStore;
    return kpiStats[groupBy]?.status || 'idle';
  }
);

export const getMetricsAndKpisByType = createSelector(
  [getImpressionsKpiChartSelector, getReportingType],
  (metricsAndKpis, type) => {
    const { kpisOptions, metricOptions } = metricsAndKpis;
    return isDigitalCampaign(type) ? [...metricOptions, ...kpisOptions] : [...metricOptions];
  }
);

export const includeCustomEvents = createSelector([getSelectedCampaigns], selectedCampaigns =>
  selectedCampaigns.some(selectedCampaign => selectedCampaign.customEvents)
);

export const getCoverageStudyReportStatus = createSelector(
  [getReportingStore],
  moduleStore => moduleStore.coverageStudyReportStatus
);

export const getContextualDiscoveryReportStatus = createSelector(
  [getReportingStore],
  moduleStore => moduleStore.contextualDiscoveryReportStatus
);

export const getPdfReportStatus = createSelector([getReportingStore, (store, key) => key], (moduleStore, key) =>
  key ? moduleStore.pdfReportStatus[key] : moduleStore.pdfReportStatus
);

export const getReportingProgressStats = createSelector(
  [getReportingStore, (store, chartType) => chartType],
  (moduleStore, chartType) => {
    if (moduleStore?.sections?.progress[chartType]) {
      return moduleStore.sections.progress[chartType];
    }
    return null;
  }
);

export const getReportingStatsGroups = createSelector([getReportingStore], moduleStore => moduleStore.sections.stats);

export const getGeoLookALikeStatus = createSelector([getReportingStore], moduleStore => moduleStore.geoLookALikeStatus);

export const reportingProgressGetMetricAndKpi = createSelector(
  [getReportingStore],
  moduleStore => moduleStore.progressEventsBy
);

export const reportingProgressGetGraphConfig = createSelector(
  [getReportingStore],
  moduleStore => moduleStore.progressGraphs
);

export const getCartography = createSelector([getReportingStore], moduleStore => moduleStore.cartography);

export const getCartographyMapConfig = createSelector(
  [getReportingStore],
  moduleStore => moduleStore.cartographyMapConfig
);

export const getCustomEvents = createSelector([getReportingStore], moduleStore => moduleStore.customEvents);

export const getMapEvents = createSelector(
  [getCampaignEvents, getDigitalConversionEvents, getHasLegacyFootfallAttribution, getHasDriveToStore],
  (
    campaignEvents = [],
    digitalConversionEvents = [],
    hasLegacyFootfallAttribution = false,
    hasDriveToStore = false
  ) => [
    ...campaignEvents,
    ...digitalConversionEvents,
    ...(hasDriveToStore ? ['fa_venue_uplift'] : []),
    ...(hasLegacyFootfallAttribution ? ['footfall_attribution'] : [])
  ]
);
